import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Monitor } from 'lucide-react'
import { ProOnlyPage } from '@/components/pro-only-page'
import { apiV2, type V2BrowserRun } from '@/lib/api-v2-client'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { StatusBadge } from '@/components/status-badge'
import { DataTable, type Column } from '@/components/data-table'
import { LoadingState, EmptyState, ErrorState } from '@/components/state-view'

export const Route = createFileRoute('/_authenticated/dashboard/monitors/$id_/browser-runs')({
  component: () => (
    <ProOnlyPage
      title="Browser runs are on Pro"
      text="Browser checks, their run history and screenshots need a paid plan."
    >
      <BrowserRunsPage />
    </ProOnlyPage>
  ),
})

/** Short relative time, e.g. "3m ago". */
function relTime(iso: string): string {
  const then = new Date(iso).getTime()
  const s = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

function BrowserRunsPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['browser-runs', id, page],
    queryFn: () => apiV2.listBrowserRuns(id, page, 20),
    refetchInterval: 30_000,
  })
  const runs = data?.data ?? []

  const columns: Column<V2BrowserRun>[] = [
    {
      header: <span className="sr-only">Screenshot</span>,
      className: 'w-24',
      cell: (r) =>
        r.screenshot_url ? (
          <img
            src={r.screenshot_url}
            alt="Run screenshot"
            loading="lazy"
            className="h-10 w-16 rounded border border-border object-cover"
          />
        ) : (
          <div className="h-10 w-16 rounded border border-border bg-muted" aria-hidden="true" />
        ),
    },
    {
      header: 'Status',
      cell: (r) =>
        r.visual_passed === false ? (
          <StatusBadge kind="degraded" label="Changed" />
        ) : (
          <StatusBadge status={r.status} />
        ),
    },
    { header: 'Steps', align: 'right', cell: (r) => `${r.step_count}` },
    { header: 'SSIM', align: 'right', cell: (r) => (r.ssim != null ? r.ssim.toFixed(3) : '—') },
    { header: 'Duration', align: 'right', cell: (r) => `${r.duration_ms.toLocaleString()} ms` },
    { header: 'When', align: 'right', cell: (r) => relTime(r.inserted_at) },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ label: 'Browser check', to: '/dashboard/browser-checks/$id', params: { id } }}
        title="Browser runs"
        description="Recent synthetic browser checks, newest first."
      />

      {isLoading ? (
        <LoadingState rows={6} />
      ) : isError ? (
        <ErrorState message="Couldn't load browser runs." onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Browser runs"
          columns={columns}
          rows={runs}
          getKey={(r) => r.check_run_id}
          onRowClick={(r) =>
            navigate({
              to: '/dashboard/monitors/$id/browser-runs/$runId',
              params: { id, runId: r.check_run_id },
            })
          }
          empty={
            <EmptyState
              icon={<Monitor className="h-6 w-6" />}
              title="No runs yet"
              description="Browser runs appear here once this monitor has executed. New monitors run within a minute."
            />
          }
        />
      )}

      {(page > 1 || runs.length === 20) && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">Page {page}</span>
          <Button variant="outline" size="sm" disabled={runs.length < 20} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
